import { useState } from "react";
import { Btn } from "../../../components/UI";
import { ShieldAlert, X, PhoneCall } from "lucide-react";

/** Allergy warning when adding a medication to the Master Medication List. Proceeding needs a short override note. */
export function AllergyOverrideModal({ pending, onCancel, onProceed }) {
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4" data-testid="allergy-override-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start gap-3 rounded-t-2xl bg-[#C62828] p-4 text-white">
          <ShieldAlert size={22} className="mt-0.5" />
          <div className="flex-1"><div className="text-[10px] font-bold uppercase opacity-80">Allergy alert</div>
            <div className="font-heading text-lg font-bold">This client may be allergic to {pending.form.medication}</div></div>
          <button type="button" data-testid="allergy-override-close" onClick={onCancel} className="rounded-full p-1 hover:bg-white/10"><X size={18} /></button>
        </div>
        <div className="space-y-3 p-4">
          <ul className="space-y-1.5">{pending.hits.map((h) => <li key={h.id} data-testid={`allergy-hit-${h.id}`} className="rounded-xl border border-[#FFCDD2] bg-[#FFEBEE] px-3 py-2 text-sm text-[#B71C1C]">Recorded allergy: <strong>{h.label}</strong></li>)}</ul>
          <div><label className="mb-1 block text-[10px] font-bold uppercase text-[#7B6157]" htmlFor="allergy-override-note">Override note (required, at least 5 characters)</label>
            <textarea id="allergy-override-note" data-testid="allergy-override-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="For example, prior reaction was a single episode years ago, benefit outweighs risk, monitor for rash"
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            <p className="mt-1 text-[10px] text-[#7B6157]">Saved on the medication record and in the audit log with your name and the time.</p></div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#F1EEE7] p-4">
          <Btn variant="ghost" data-testid="allergy-override-cancel" onClick={onCancel}>Review again</Btn>
          <Btn data-testid="allergy-override-proceed" disabled={note.trim().length < 5} onClick={() => onProceed(note.trim())}>Override and add</Btn>
        </div>
      </div>
    </div>
  );
}

/** After a prescribed medication is discontinued: remind staff to call the pharmacy to cancel remaining refills. */
export function PharmacyPromptDialog({ prompt, onClose, onOpenRx }) {
  const ph = prompt.pharmacy;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4" data-testid="pharmacy-prompt-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-2 flex items-center gap-2 font-heading text-lg font-bold text-[#3E2723]"><PhoneCall size={18} className="text-[#8D5A00]" /> Call the pharmacy</div>
        <p className="text-sm text-[#5D4037]">{prompt.medication} on {prompt.rx_number} is now discontinued and locked. It can no longer be printed, emailed, or duplicated.</p>
        {ph ? <p className="mt-2 rounded-xl bg-[#FFF3E0] px-3 py-2 text-sm font-semibold text-[#8D5A00]" data-testid="pharmacy-prompt-who">Call {ph.name}{ph.phone ? ` at ${ph.phone}` : ""} to cancel any remaining refills, then log the call on the prescription.</p>
          : <p className="mt-2 text-sm text-[#7B6157]">No pharmacy is linked to this prescription, so there are no refills on file to cancel.</p>}
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" data-testid="pharmacy-prompt-later" onClick={onClose}>Later</Btn>
          {ph && <Btn data-testid="pharmacy-prompt-open" onClick={() => onOpenRx(prompt.rx_id)}>Open prescription to log the call</Btn>}
        </div>
      </div>
    </div>
  );
}
